import type { MeetingDetail, SourceReference, SummaryItem } from '@shared/contract';
import { formatDuration } from '../lib/format';
import { References } from './References';

/** "på fredag (6 nov)" när datumet är känt, annars orden som de sades. */
function dueLabel(due: string, dueDate: string | null): string {
  if (!dueDate) return due;
  const date = new Date(`${dueDate}T12:00:00Z`).toLocaleDateString('sv-SE', { timeZone: 'UTC', day: 'numeric', month: 'short' });
  return `${due} (${date})`;
}

interface Props {
  detail: MeetingDetail;
  pending: boolean;
  failed: boolean;
  onRequest: () => void;
  onJump: (ref: SourceReference) => void;
}

function Section({ title, items, empty, onJump }: { title: string; items: SummaryItem[]; empty: string; onJump: (ref: SourceReference) => void }) {
  return (
    <div className="summary-section">
      <h4>{title}</h4>
      {items.length === 0 ? (
        <p className="muted small">{empty}</p>
      ) : (
        <ul>
          {items.map((i, n) => (
            <li key={n}>
              {i.text} <References refs={i.references} onJump={onJump} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function SummaryPanel({ detail, pending, failed, onRequest, onJump }: Props) {
  const summary = detail.summaries.find((s) => s.is_final) ?? detail.summaries[0];
  const canRequest = detail.status !== 'created';
  const working = pending || detail.status === 'finishing';
  return (
    <section className="card">
      <div className="card-head">
        <h3>Sammanfattning</h3>
        {canRequest && (
          <button className="btn small" onClick={onRequest} disabled={working}>
            {working ? 'Sammanfattar…' : detail.status === 'completed' ? 'Gör om' : 'Sammanfatta nu'}
          </button>
        )}
      </div>
      {failed && <div className="alert error small">Sammanfattningen kunde inte skapas. Försök igen om en stund.</div>}
      {!summary ? (
        <p className="muted">
          {working
            ? 'Sammanfattningen skapas…'
            : canRequest
              ? 'Ingen sammanfattning ännu. Du kan begära en när som helst utan att stoppa inspelningen.'
              : 'Starta mötet för att kunna sammanfatta.'}
        </p>
      ) : (
        <div className="summary">
          <p className="muted small">
            {summary.is_final ? 'Slutlig sammanfattning' : `Preliminär, omfattar fram till ${formatDuration(summary.covers_until_ms)}`}
            {' · '}
            {new Date(summary.created_at).toLocaleTimeString('sv-SE', { timeStyle: 'short' })}
          </p>
          {summary.summary.insufficient_material && (
            <div className="alert warn small">Underlaget är för litet för en fullständig sammanfattning.</div>
          )}
          <p>{summary.summary.short_summary}</p>
          <Section title="Viktigaste diskussionspunkterna" items={summary.summary.key_points} empty="Inga identifierade." onJump={onJump} />
          <Section title="Beslut" items={summary.summary.decisions} empty="Inga beslut kunde identifieras i transkriptionen." onJump={onJump} />
          <div className="summary-section">
            <h4>Att göra</h4>
            {summary.summary.action_items.length === 0 ? (
              <p className="muted small">Inga uppgifter kunde identifieras.</p>
            ) : (
              <ul className="todo-list">
                {summary.summary.action_items.map((a, n) => (
                  <li key={n}>
                    {a.text}
                    {(a.owner || a.due) && (
                      <span className="todo-meta">
                        {a.owner && <strong>{a.owner}</strong>}
                        {a.owner && a.due && ' · '}
                        {a.due && <span title={a.due_date ?? undefined}>{dueLabel(a.due, a.due_date)}</span>}
                      </span>
                    )}{' '}
                    <References refs={a.references} onJump={onJump} />
                  </li>
                ))}
              </ul>
            )}
          </div>
          <Section title="Öppna frågor" items={summary.summary.open_questions} empty="Inga identifierade." onJump={onJump} />
        </div>
      )}
    </section>
  );
}
